import { ArrowUpRight, BookOpen, ExternalLink } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

const PILLARS = [
  {
    title: "Biocompatible interfaces",
    body: "Soft, silk-inspired material concepts aimed at supporting long-term signal compatibility versus rigid traditional probes.",
    refs: [
      {
        label: "Silk-based bioelectronics — review",
        href: "https://www.nature.com/articles/s41578-019-0150-z",
      },
      {
        label: "Flexible neural interfaces — perspective",
        href: "https://www.science.org/doi/10.1126/science.aaa9647",
      },
    ],
  },
  {
    title: "Scalable neural signal collection",
    body: "Designed around higher-density recordings and durable signal channels so models receive richer real-time data.",
    refs: [
      {
        label: "High-density neural probes",
        href: "https://www.nature.com/articles/nature24636",
      },
    ],
  },
  {
    title: "Physics-aware AI",
    body: "Modeling that incorporates biological priors and uncertainty quantification — interpretable, not opaque.",
    refs: [
      {
        label: "Physics-informed neural networks (PINNs)",
        href: "https://arxiv.org/abs/1711.10561",
      },
      {
        label: "Probabilistic ML for neuroscience",
        href: "https://www.cell.com/neuron/fulltext/S0896-6273(20)30185-2",
      },
    ],
  },
  {
    title: "Closed-loop biofeedback",
    body: "Adaptive feedback systems aimed at responding to individual circuit dynamics, supporting future neuroplasticity research.",
    refs: [
      {
        label: "Closed-loop neuromodulation — review",
        href: "https://www.nature.com/articles/s41583-021-00499-9",
      },
    ],
  },
];

const FAQ = [
  {
    q: "Is Neurobolt.AI a medical device or clinical product?",
    a: "No. Neurobolt.AI is a research-stage platform. We do not claim to cure, treat, diagnose, reverse, or clinically validate outcomes. Listed applications are platform directions for future research.",
  },
  {
    q: "What does the closed-loop platform actually do today?",
    a: "It is designed to combine biocompatible interface concepts, neural activity mapping, interpretable physics-aware AI, and personalized biofeedback into one loop — built toward continuous capture-model-feedback cycles.",
  },
  {
    q: "How can research partners engage?",
    a: "Reach out via the contact form or email contact@neurobolt.ai. We're open to collaboration with universities, labs, clinicians, and operators.",
  },
];

export default function ResearchPage({ onOpenContact }) {
  useReveal();
  return (
    <main className="pt-24 md:pt-32 pb-24">
      <section className="max-w-[1180px] mx-auto px-6 lg:px-12">
        <div className="reveal flex items-center gap-3 mb-6">
          <span className="pill">
            <span className="h-1.5 w-1.5 rounded-full bg-[#38BDF8] nb-pulse" />
            Research
          </span>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#475569]">
            / Science
          </span>
        </div>

        <h1 className="reveal font-display text-white text-[clamp(36px,5.5vw,68px)] leading-[1.05] font-medium tracking-tight max-w-3xl headline-glow">
          The science behind a closed-loop, adaptive neuroplasticity platform.
        </h1>

        <p className="reveal mt-6 text-[#CBD5E1] text-[17px] leading-relaxed max-w-2xl">
          Neurobolt.AI's platform direction is grounded in four pillars: biocompatible interface concepts, scalable neural signal collection, physics-aware AI modeling, and adaptive biofeedback. Below are the principles guiding our research-stage work and selected external references.
        </p>

        <div className="reveal mt-10 flex flex-wrap gap-3">
          <a
            href="/"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white text-[13px] hover:border-[#38BDF8]/50"
          >
            ← Back to home
          </a>
          <button
            onClick={onOpenContact}
            className="btn-glow inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#020617] text-[13px] font-semibold hover:bg-[#BFDBFE]"
          >
            Partner with research
          </button>
        </div>
      </section>

      <section className="max-w-[1180px] mx-auto px-6 lg:px-12 mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start">
          <div className="glass rounded-2xl p-7">
            <div className="flex items-center gap-2 text-[#38BDF8]">
              <BookOpen size={16} />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em]">
                On this page
              </span>
            </div>
            <ul className="mt-5 space-y-3 text-[14px]">
              {PILLARS.map((p, i) => (
                <li key={i}>
                  <a
                    href={`#pillar-${i}`}
                    className="text-[#CBD5E1] hover:text-white link-underline"
                  >
                    {String(i + 1).padStart(2, "0")} — {p.title}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="#faq"
                  className="text-[#CBD5E1] hover:text-white link-underline"
                >
                  05 — FAQ
                </a>
              </li>
            </ul>

            <div className="mt-7 border-t border-white/10 pt-5">
              <p className="text-[12.5px] text-[#64748B] leading-relaxed">
                External references are provided to contextualize the research
                space. They are not endorsements and do not represent Neurobolt
                claims.
              </p>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-8 space-y-14">
          {PILLARS.map((p, i) => (
            <article
              key={i}
              id={`pillar-${i}`}
              className="reveal scroll-mt-28"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="section-num">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="h-px w-8 bg-white/15" />
                <span className="section-num !text-[#38BDF8]">Pillar</span>
              </div>
              <h2 className="font-display text-white text-[28px] lg:text-[34px] font-medium leading-tight">
                {p.title}
              </h2>
              <p className="mt-4 text-[#CBD5E1] text-[15.5px] leading-relaxed max-w-2xl">
                {p.body}
              </p>
              <ul className="mt-6 space-y-2">
                {p.refs.map((r, j) => (
                  <li key={j}>
                    <a
                      href={r.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center gap-2 text-[14px] text-[#BFDBFE] hover:text-white"
                    >
                      <ExternalLink
                        size={13}
                        className="text-[#38BDF8] shrink-0 mt-px"
                      />
                      <span className="link-underline">{r.label}</span>
                      <ArrowUpRight
                        size={13}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </article>
          ))}

          <article id="faq" className="reveal scroll-mt-28 pt-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="section-num">05</span>
              <span className="h-px w-8 bg-white/15" />
              <span className="section-num !text-[#38BDF8]">FAQ</span>
            </div>
            <h2 className="font-display text-white text-[28px] lg:text-[34px] font-medium leading-tight">
              Common questions
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-3">
              {FAQ.map((f, i) => (
                <details
                  key={i}
                  className="group glass rounded-2xl p-6 transition-colors open:border-[#38BDF8]/30"
                >
                  <summary className="flex items-start gap-4 cursor-pointer list-none">
                    <span className="font-mono text-[10px] tracking-[0.3em] text-[#475569] mt-1.5">
                      0{i + 1}
                    </span>
                    <span className="font-display text-white text-[18px] font-medium flex-1">
                      {f.q}
                    </span>
                    <span className="text-[#38BDF8] text-[20px] leading-none transition-transform group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <p className="mt-4 text-[#94A3B8] text-[14.5px] leading-relaxed pl-9">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </article>

          <div className="reveal mt-4 rounded-2xl border border-dashed border-white/10 p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#38BDF8]">
              Claim safety
            </div>
            <p className="mt-3 text-[14px] text-[#CBD5E1] leading-relaxed">
              All content on this page uses research-stage language. Neurobolt.AI does not claim to cure, treat, diagnose, reverse, repair, or clinically prove outcomes. Future applications listed represent platform direction, not clinical claims.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
